import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, ArrowLeft, Trash2, RefreshCw, AlertTriangle, Clock, Download, Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
/* TANDA 12 (firma #182) — LA DIRECCIÓN SALE DE LA FUENTE ÚNICA.
   Aquí vivía el correo escrito en duro, y era la última copia (con su gemela de
   `AdminDashboard.js`) de una regla que ya vivía por variable de entorno en las
   otras cuatro superficies. Dos mecanismos para la misma pregunta es uno que se
   puede quedar atrás; ahora hay uno. Vive en `components/routeGuards.js`, que es
   además quien guarda esta ruta. Guarda: cero correos en duro en `src/`. */
import { ADMIN_EMAIL } from '../components/routeGuards';
/* Cierre de post-t7-1, Bloque D (#605): la página separa Fallas, Avisos y Eventos, un
   renglón por documento, con una frase en español y el correo del usuario siempre. La
   clase y la frase las decide el servidor (services/registro_errores.py); aquí sólo se
   pintan. Si la respuesta no trae los grupos (el servidor de antes, durante el
   despliegue), se pintan las filas como antes. Los textos de esta página van en español
   (sólo es del admin). */
import { TEXTOS_REGISTRO as T, PESTANAS, cuandoEnMexico, colorDeClase, nombreDeClase } from '../lib/registroErrores';

// post-t7-2b (D3, 5.7): el aviso de quien no puede ver el registro (no es admin, o el servidor contesta 403). Solo
// espanol: la pagina es del admin.
const ACCESO_DENEGADO = 'Acceso denegado.';

function DetalleDeFila({ err }) {
  const correo = err.correo || err.user_email || err.details?.user_email || '';
  const documento = err.details?.document_id || '';
  return (
    <div className="bg-[#0a0a0a] rounded-lg p-3 mt-2 text-xs font-mono space-y-1.5 break-words" data-testid="error-fila-cruda">
      <div className="text-[#a0a0a0]">{err.type || 'ERROR'}</div>
      <div>
        <span className="text-[#555]">{T.mensaje}: </span>
        <span className="text-[#ccc]">{err.message}</span>
      </div>
      {correo && (
        <div>
          <span className="text-[#555]">{T.usuario}: </span>
          <span className="text-[#FF6B00]">{correo}</span>
          {err.dueno_correo && <span className="text-[#ccc]"> ({T.compra_de} {err.dueno_correo})</span>}
        </div>
      )}
      {documento && (
        <div>
          <span className="text-[#555]">{T.documento}: </span>
          <span className="text-[#ccc]">{documento}</span>
        </div>
      )}
      {err.details && Object.keys(err.details).length > 0 && (
        <div>
          <span className="text-[#555]">{T.detalles}: </span>
          <span className="text-[#a0a0a0]">{JSON.stringify(err.details, null, 2)}</span>
        </div>
      )}
      <div>
        <span className="text-[#555]">{T.cuando}: </span>
        <span className="text-[#a0a0a0]">{cuandoEnMexico(err.created_at)}</span>
      </div>
    </div>
  );
}

export default function AdminErrorsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const [grupos, setGrupos] = useState(null);
  const [cuentas, setCuentas] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [pestana, setPestana] = useState('falla');
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [totalErrors, setTotalErrors] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchErrors = useCallback(async (emailParam = '') => {
    setLoading(true);
    try {
      const params = emailParam ? `?email=${encodeURIComponent(emailParam)}` : '';
      const res = await api.get(`/sb-ctrl/errors${params}`);
      setErrors(res.data.errors || []);
      setGrupos(Array.isArray(res.data.grupos) ? res.data.grupos : null);
      setCuentas(res.data.cuentas || null);
      setResumen(res.data.resumen || null);
      setTotalErrors(res.data.total || res.data.count || 0);
      setActiveFilter(emailParam);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(ACCESO_DENEGADO);
        navigate('/dashboard');
      } else {
        toast.error(T.no_cargo);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!ADMIN_EMAIL || user?.email?.toLowerCase() !== ADMIN_EMAIL) {
      toast.error(ACCESO_DENEGADO);
      navigate('/dashboard');
      return;
    }
    fetchErrors();
  }, [user, navigate, fetchErrors]);

  const handleSearch = () => {
    if (emailFilter.trim()) fetchErrors(emailFilter.trim());
  };

  const handleClearFilter = () => {
    setEmailFilter('');
    fetchErrors('');
  };

  const handleClear = async () => {
    if (!window.confirm(T.confirmar_borrar)) return;
    setClearing(true);
    try {
      await api.delete('/sb-ctrl/errors');
      setErrors([]);
      setGrupos((g) => (g ? [] : g));
      setCuentas((c) => (c ? { falla: 0, aviso: 0, evento: 0, todo: 0 } : c));
      setTotalErrors(0);
      toast.success(T.borrado);
    } catch {
      toast.error(T.no_borro);
    } finally {
      setClearing(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/sb-ctrl/errors/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `studybuddy_errors_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(T.exportado);
    } catch {
      toast.error(T.no_exporto);
    } finally {
      setExporting(false);
    }
  };

  if (loading && errors.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  const conGrupos = Array.isArray(grupos);
  const visibles = conGrupos ? grupos.filter((g) => pestana === 'todo' || g.clase === pestana) : [];
  const vacio = conGrupos ? visibles.length === 0 : errors.length === 0;

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="admin-errors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}
            className="text-[#6a6a6a] hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" data-testid="admin-errors-title">
              <AlertTriangle className="w-5 h-5 text-[#FF6B00]" />
              {T.titulo}
            </h1>
            <p className="text-[#6a6a6a] text-xs" data-testid="admin-errors-total">
              {T.total(totalErrors)}{activeFilter && ` (${T.buscando}: "${activeFilter}")`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || totalErrors === 0}
            className="border-[#262626] text-[#a0a0a0] hover:text-white hover:bg-[#1f1f1f] rounded-xl"
            data-testid="export-btn">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            {T.exportar}
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchErrors(activeFilter)}
            className="border-[#262626] text-[#a0a0a0] hover:text-white hover:bg-[#1f1f1f] rounded-xl"
            data-testid="refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> {T.actualizar}
          </Button>
          <Button variant="outline" size="sm" onClick={handleClear} disabled={clearing || errors.length === 0}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl"
            data-testid="clear-btn">
            {clearing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
            {T.borrar_todo}
          </Button>
        </div>
      </div>

      {resumen && (
        <div className="glass-card rounded-[12px] px-4 py-3 text-sm text-[#ccc] space-y-1" data-testid="errores-resumen">
          {['7', '30'].filter((k) => resumen[k]).map((k) => (
            <p key={k} data-testid={`errores-resumen-${k}`}>{resumen[k].texto}</p>
          ))}
        </div>
      )}

      {/* Search bar */}
      <div className="flex gap-2" data-testid="error-search-bar">
        <Input value={emailFilter} onChange={e => setEmailFilter(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder={T.buscar}
          className="flex-1 bg-[#0d0d0d] border-[#262626] text-white rounded-xl h-9 text-sm"
          data-testid="error-email-search-input" />
        <Button size="sm" onClick={handleSearch} disabled={!emailFilter.trim()}
          className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-9 px-3"
          data-testid="error-search-btn">
          <Search className="w-3.5 h-3.5" />
        </Button>
        {activeFilter && (
          <Button size="sm" variant="outline" onClick={handleClearFilter}
            className="border-[#333] text-[#aaa] hover:text-white rounded-xl h-9 px-3"
            data-testid="error-clear-filter-btn">
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {conGrupos && (
        <div className="flex flex-wrap gap-2" role="tablist" data-testid="errores-pestanas">
          {PESTANAS.map(({ clave, rotulo }) => (
            <Button key={clave} size="sm" role="tab" aria-selected={pestana === clave}
              variant={pestana === clave ? 'default' : 'outline'}
              onClick={() => { setPestana(clave); setExpandedId(null); }}
              className={pestana === clave ? 'bg-[#FF6B00] text-white rounded-xl' : 'border-[#333] text-[#aaa] rounded-xl'}
              data-testid={`errores-pestana-${clave}`}>
              {rotulo} ({(cuentas && cuentas[clave]) || 0})
            </Button>
          ))}
        </div>
      )}

      {vacio ? (
        <div className="glass-card rounded-[20px] p-12 text-center" data-testid="errores-vacio">
          <AlertTriangle className="w-12 h-12 text-[#333] mx-auto mb-3" />
          <h3 className="text-base font-semibold text-white mb-1">
            {activeFilter ? T.nada_coincide : T.nada_aqui}
          </h3>
        </div>
      ) : conGrupos ? (
        <div className="space-y-1">
          {visibles.map((g, i) => {
            const isExpanded = expandedId === g.clave;
            return (
              <div key={g.clave} className="glass-card rounded-[12px] overflow-hidden" data-testid={`error-${i}`}>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : g.clave)}
                  className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#1a1a1a]/50 transition-colors"
                  data-testid={`error-toggle-${i}`}
                >
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 text-[#555] shrink-0 mt-0.5" />
                    : <ChevronRight className="w-3.5 h-3.5 text-[#555] shrink-0 mt-0.5" />
                  }
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Badge variant="outline" className={`text-[10px] shrink-0 ${colorDeClase(g.clase)}`}
                        data-testid={`error-clase-${i}`}>
                        {nombreDeClase(g.clase)}
                      </Badge>
                      <span className="text-sm text-[#ccc] break-words min-w-0" data-testid={`error-frase-${i}`}>{g.frase}</span>
                      {g.n > 1 && <span className="text-[11px] text-[#a0a0a0] shrink-0" data-testid={`error-n-${i}`}>x {g.n}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#a0a0a0]">
                      {g.archivo && <span className="break-words min-w-0" data-testid={`error-archivo-${i}`}>{g.archivo}</span>}
                      {g.correo && <span className="text-[#FF6B00] break-all" data-testid={`error-correo-${i}`}>{g.correo}</span>}
                      {g.dueno_correo && (
                        <span className="break-all" data-testid={`error-dueno-${i}`}>{T.compra_de} {g.dueno_correo}</span>
                      )}
                      <span className="flex items-center gap-1 whitespace-nowrap" data-testid={`error-cuando-${i}`}>
                        <Clock className="w-3 h-3" />{cuandoEnMexico(g.cuando)}
                      </span>
                    </div>
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-3 pt-0 ml-7 border-t border-[#1a1a1a]" data-testid={`error-detail-${i}`}>
                    {(g.filas || errors.filter((e) => e.grupo === g.clave)).map((err, k) => (
                      <DetalleDeFila key={err.id || k} err={err} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-1">
          {errors.map((err, i) => {
            const errId = err.id || i;
            const isExpanded = expandedId === errId;
            const email = err.user_email || err.details?.user_email || '';
            return (
              <div key={errId} className="glass-card rounded-[12px] overflow-hidden" data-testid={`error-${i}`}>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : errId)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#1a1a1a]/50 transition-colors"
                  data-testid={`error-toggle-${i}`}
                >
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 text-[#555] shrink-0" />
                    : <ChevronRight className="w-3.5 h-3.5 text-[#555] shrink-0" />
                  }
                  <Badge variant="outline" className="text-[10px] shrink-0 bg-red-500/15 text-red-400 border-red-500/20">
                    {err.type || 'ERROR'}
                  </Badge>
                  <span className="flex-1 text-xs text-[#ccc] truncate font-mono">
                    {err.message}
                  </span>
                  {email && (
                    <span className="text-[11px] text-[#555] shrink-0 hidden sm:block">{email}</span>
                  )}
                  <span className="flex items-center gap-1 text-[10px] text-[#444] shrink-0">
                    <Clock className="w-3 h-3" />
                    <span className="whitespace-nowrap">{cuandoEnMexico(err.created_at)}</span>
                  </span>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-3 pt-0 ml-7 border-t border-[#1a1a1a]" data-testid={`error-detail-${i}`}>
                    <DetalleDeFila err={err} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
